/**
 * demoMode — the switch, and the reasons it is shaped the way it is.
 *
 * OFF UNLESS SOMEBODY TURNED IT ON IN THIS TAB. The flag lives in
 * sessionStorage, not localStorage and not a list: a new tab starts clean, and
 * closing the browser ends it. A payroll module that could be left in demo by
 * a colleague three days ago, on a machine somebody else then does a real run
 * on, is a worse problem than having no demo at all.
 *
 * IT CANNOT REACH SHAREPOINT. Nothing here is ever written to a list, and the
 * store it switches on refuses every write it does not implement itself. The
 * flag is read by SubconPayrollService and by useSubconPayroll, and those are
 * the only two doors into payroll's data.
 *
 * IT IS LOUD. Every payroll screen carries a banner while it is on, because
 * the one failure that matters here is somebody believing a demo figure.
 */

const KEY = "isyn.subcon.demo.v1";

type Listener = () => void;
const listeners = new Set<Listener>();

let cached: boolean | null = null;

function read(): boolean {
  if (cached !== null) return cached;
  try {
    cached = sessionStorage.getItem(KEY) === "on";
  } catch {
    // Private windows and embedded webviews can throw on access. No storage
    // means no demo, which is the safe direction.
    cached = false;
  }
  return cached;
}

/** True when this tab is running on invented data. */
export function isDemo(): boolean {
  return read();
}

/** Turn the demo on or off for this tab, and tell every screen watching. */
export function setDemo(on: boolean): void {
  cached = on;
  try {
    if (on) sessionStorage.setItem(KEY, "on");
    else sessionStorage.removeItem(KEY);
  } catch {
    // Not being able to remember it across a reload is survivable; the flag
    // still holds for this page, and a reload lands back on real data.
  }
  for (const fn of listeners) fn();
}

/** Watch the flag. Returns the unsubscribe. */
export function onDemoChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * The methods that CHANGE something.
 *
 * The demo store implements what the six-step walkthrough needs and no more.
 * Anything else has to fail closed: a write that fell through to the live
 * service while a banner said "demo" would put invented money in a real list,
 * which is the one outcome this whole module exists to prevent. Reads may fall
 * through — reading the real payslip letterhead makes the demo payslip look
 * like the real thing, and reading cannot damage anything.
 */
export const PAYROLL_WRITE_METHODS: ReadonlySet<string> = new Set([
  "createProfile", "updateProfile", "setProfileActive", "seedProfiles", "applyBankDetails",
  "seedRateCard", "updateRate",
  "createPayout", "importPayouts", "updatePayout", "setPayoutStatus", "deleteDraft",
  "setPayslipLogo", "clearPayslipLogo",
  "setBizlinkTemplate", "clearBizlinkTemplate", "setNonBpiDetail", "setNonBpiDetails", "setNonBpiDefaults",
  "setPayslipForm",
  "setPantsChoice", "clearPantsChoice",
  "setSewerAlias",
  "setWipPlan", "clearWipPlan",
  "setWipPants", "clearWipPants", "setKeyPayable", "setLineEdit", "clearLineEdits",
  "setKeyOverride", "clearKeyOverride", "setKeyOverrides", "clearKeyOverrides",
  "addWorkbookPaidKeys",
  "removeWorkbookPaidKeys",
]);
